import React from 'react';
import withSizes from 'react-sizes';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { v4 } from 'uuid';
import { Field } from 'formik';
import InputMask from 'react-input-mask';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import InputAdornment from '@material-ui/core/InputAdornment';
import { TextField } from '@material-ui/core';
import { Input, InputMasked, Textarea } from '@sunrun/component-library';
import mapSizesToProps from './utilities/mapSizesToProps';
import { useStyles } from './InputGroupStyles';

const StyledWrapper = styled.div`
  textarea {
    border-radius: 8px;
  }
`;

export const CustomInputComponent = ({
  endpointerror,
  field,
  form, // This is pulled out from the spread to ensure correct enter key functionality
  includeForm,
  mask,
  onBlur,
  onChange,
  required,
  textarea,
  materialTextField,
  materialMask,
  isMobile,
  ...props
}) => {
  const error = form.touched[field.name] && form.errors[field.name];
  // if input is required, only set valid as true if there is a value, too
  const valid = required ? form.touched[field.name] && !error && field.value : form.touched[field.name] && !error;
  const validationProps = {
    isSuccessful: !!valid,
    hasError: !!error,
    helperText: error,
  };

  const constructedProps = { ...props };
  if (onChange) {
    // There is a custom onChange event, so we need to fire it and the formik update
    const newOnChange = e => {
      form.handleChange(e);
      onChange(e);
    };
    constructedProps.onChange = newOnChange;
  }

  if (onBlur) {
    const newOnBlur = e => {
      form.handleBlur(e);
      onBlur(e);
    };
    constructedProps.onBlur = newOnBlur;
  }

  if (includeForm) {
    constructedProps.form = form;
  }

  const classes = useStyles();

  const getClassForMaterialEl = (hasError, isSuccessful) => {
    let className = classes.defaultColors;
    if (hasError) {
      className = classes.warningColors;
    } else if (isSuccessful) {
      className = classes.successColors;
    }
    return className;
  };

  const getIconForMaterialEl = (hasError, isSuccessful, mobileSize) => {
    let icon = <div />;
    if (mobileSize) {
      icon = <div />;
    } else if (hasError) {
      icon = <ErrorOutlineIcon className={classes.errorIcon} />;
    } else if (isSuccessful) {
      icon = <CheckCircleOutlineIcon className={classes.successIcon} />;
    }
    return icon;
  };

  let customInputComponent = null;
  if (materialMask) {
    customInputComponent = (
      <InputMask mask="(999) 999-9999" value={field.value} disabled={false} maskChar=" " onBlur={field.onBlur} onChange={field.onChange}>
        {() => (
          <TextField
            className={getClassForMaterialEl(validationProps.hasError, validationProps.isSuccessful)}
            helperText={validationProps.helperText}
            label={constructedProps.label}
            id={constructedProps.id}
            data-testid={field.name}
            name={field.name}
            type={field.type}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{getIconForMaterialEl(validationProps.hasError, validationProps.isSuccessful, isMobile)}</InputAdornment>
              ),
            }}
          />
        )}
      </InputMask>
    );
  } else if (materialTextField) {
    customInputComponent = (
      <TextField
        className={getClassForMaterialEl(validationProps.hasError, validationProps.isSuccessful)}
        {...field}
        {...constructedProps}
        helperText={validationProps.helperText}
        fullWidth
        variant="outlined"
        size="small"
        margin="dense"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{getIconForMaterialEl(validationProps.hasError, validationProps.isSuccessful, isMobile)}</InputAdornment>
          ),
        }}
      />
    );
  } else if (mask) {
    customInputComponent = <InputMasked {...field} {...constructedProps} mask={mask} guide={false} {...validationProps} />;
  } else if (textarea) {
    delete constructedProps.helperText;
    customInputComponent = (
      <StyledWrapper>
        <Textarea {...field} {...constructedProps} hasError={validationProps.hasError} />;
      </StyledWrapper>
    );
  } else {
    customInputComponent = <Input {...field} {...constructedProps} {...validationProps} />;
  }
  return customInputComponent;
};

CustomInputComponent.propTypes = {
  endpointerror: PropTypes.string,
  field: PropTypes.shape({}).isRequired,
  form: PropTypes.shape({}).isRequired,
  includeForm: PropTypes.bool,
  mask: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  textarea: PropTypes.bool,
  materialTextField: PropTypes.bool,
  helperText: PropTypes.string,
};

CustomInputComponent.defaultProps = {
  endpointerror: null,
  includeForm: false,
  mask: null,
  onBlur: null,
  onChange: null,
  required: null,
  textarea: false,
  materialTextField: false,
  materialMask: false,
  helperText: '',
};

const InputGroupWrapper = styled.div`
  input {
    background-color: transparent;
  }
`;

const InputGroup = ({ label, name, ...props }) => {
  const id = v4();
  return (
    <InputGroupWrapper>
      <Field component={CustomInputComponent} name={name} id={id} label={label} {...props} />
    </InputGroupWrapper>
  );
};

InputGroup.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  mask: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
};

InputGroup.defaultProps = {
  label: null,
  mask: null,
};

export default withSizes(mapSizesToProps)(InputGroup);
